import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Collapse,
  Input,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from "antd";
import {
  CheckCircleFilled,
  FolderOpenOutlined,
  ReloadOutlined,
  RobotOutlined,
  WarningFilled
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Error, Success } from "../../../../commons/Messages/Messages";
import { getErrorMessage } from "../mailApi";
import { formatSize } from "../mailUtils";
import { useMailTranslation } from "../useMailTranslation";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import AssistantConsentModal from "./AssistantConsentModal";
import { CATEGORY_LABELS, PRIORITY_COLORS, PRIORITY_LABELS } from "./assistantLabels";

const { Paragraph, Text } = Typography;

/** One row per attachment: keep it or not, where it goes, under which name. */
const buildDrafts = (analysis, message) => {
  const suggestions = new Map((analysis?.attachments || []).map(item => [item.index, item]));
  return message.attachments
    .filter(attachment => !attachment.inline)
    .map(attachment => {
      const suggestion = suggestions.get(attachment.index);
      return {
        index: attachment.index,
        filename: attachment.filename,
        size: attachment.size,
        documentType: suggestion?.documentType || "",
        reason: suggestion?.reason || "",
        saved: Boolean(suggestion?.document),
        savedPath: suggestion?.savedPath,
        selected: Boolean(suggestion?.keep) && !suggestion?.document,
        directoryPath: suggestion?.savedPath || suggestion?.directoryPath || "",
        fileName: suggestion?.fileName || attachment.filename
      };
    });
};

const AssistantPanel = ({ assistant, account, message, onCompose, onMove }) => {
  const { t } = useMailTranslation();
  // Touch-sized controls on phones, compact ones in the desktop reader.
  const controlSize = useIsMobile() ? "middle" : "small";
  const [consentOpen, setConsentOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [drafts, setDrafts] = useState([]);
  const analysis = assistant.analysisOf(message);
  const status = assistant.status;

  const attachmentsKey = JSON.stringify(
    (analysis?.attachments || []).map(item => [item.index, item.document, item.directoryPath])
  );
  useEffect(() => {
    setDrafts(buildDrafts(analysis, message));
  }, [message.uid, analysis?.status, attachmentsKey]);

  useEffect(() => {
    if (analysis?.status === "done" && drafts.length && !assistant.directory) {
      assistant.loadDirectory().catch(() => {});
    }
  }, [analysis?.status, drafts.length]);

  const directoryOptions = useMemo(
    () => (assistant.directory || []).map(item => ({ value: item.path })),
    [assistant.directory]
  );

  if (!status) return null;
  if (!status.configured) return null;

  const run = async (key, fn, errorText) => {
    setBusy(key);
    try {
      await fn();
    } catch (error) {
      Error(getErrorMessage(error, t(errorText)));
    } finally {
      setBusy(null);
    }
  };

  if (!status.consentAt) {
    return (
      <div className="mail-assistant mail-assistant--compact">
        <RobotOutlined />
        <span>
          {t("Let the assistant sort this email, suggest a reply and file its attachments.")}
        </span>
        <Button size={controlSize} onClick={() => setConsentOpen(true)}>
          {t("Turn on")}
        </Button>
        <AssistantConsentModal
          open={consentOpen}
          email={account.email}
          onCancel={() => setConsentOpen(false)}
          onConfirm={async () => {
            await assistant.setConsent(true);
            setConsentOpen(false);
          }}
        />
      </div>
    );
  }

  const analyze = force =>
    run("analyze", () => assistant.analyze([message], { force }), "Failed to start the analysis");

  if (!analysis || (analysis.status === "failed" && busy !== "analyze")) {
    return (
      <div className="mail-assistant mail-assistant--compact">
        <RobotOutlined />
        {analysis?.status === "failed" ? (
          <Text type="danger">{t(analysis.error || "The analysis failed.")}</Text>
        ) : (
          <span>{t("Sort this email, get a reply suggestion and file its attachments.")}</span>
        )}
        <Button
          size={controlSize}
          type="primary"
          loading={busy === "analyze"}
          onClick={() => analyze(true)}
        >
          {analysis?.status === "failed" ? t("Try again") : t("Analyse")}
        </Button>
      </div>
    );
  }

  if (analysis.status === "processing") {
    return (
      <div className="mail-assistant mail-assistant--compact">
        <Spin size="small" />
        <span>{t("The assistant is reading this email…")}</span>
      </div>
    );
  }

  const openReply = () => onCompose("reply", message, { prefillBody: analysis.reply?.body || "" });

  const redraft = () =>
    run(
      "reply",
      async () => {
        await assistant.redraftReply(message, instructions);
        setInstructions("");
      },
      "Failed to draft a reply"
    );

  const setDraft = (index, patch) =>
    setDrafts(prev => prev.map(item => (item.index === index ? { ...item, ...patch } : item)));

  const selected = drafts.filter(item => item.selected && !item.saved);
  const saveSelected = () =>
    run(
      "save",
      async () => {
        const res = await assistant.saveAttachments(
          message,
          selected.map(item => ({
            index: item.index,
            directoryPath: item.directoryPath,
            fileName: item.fileName
          }))
        );
        const saved = res.data.filter(result => result.ok).length;
        const failed = res.data.filter(result => !result.ok);
        if (saved) Success(t("Saved to the directory: {{count}}", { count: saved }));
        failed.forEach(result => {
          const name = drafts.find(item => item.index === result.index)?.filename || "";
          Error(`${name}: ${t(result.error)}`);
        });
        assistant.loadDirectory().catch(() => {});
      },
      "Failed to save attachments"
    );

  const replyBusy = analysis.replyStatus === "processing" || busy === "reply";
  const partial = analysis.truncated || analysis.unreadAttachments?.length > 0;

  return (
    <div className="mail-assistant">
      <div className="mail-assistant-head">
        <Space size={4} wrap>
          <RobotOutlined className="mail-assistant-icon" />
          <Tag color={PRIORITY_COLORS[analysis.priority]}>
            {t(PRIORITY_LABELS[analysis.priority] || analysis.priority)}
          </Tag>
          <Tag>{t(CATEGORY_LABELS[analysis.category] || analysis.category)}</Tag>
          {analysis.deadline && (
            <Tag color="volcano">{t("Deadline {{date}}", { date: analysis.deadline })}</Tag>
          )}
          {analysis.requiresReply && <Tag color="geekblue">{t("Needs a reply")}</Tag>}
        </Space>
        <Tooltip title={t("Analyse again")}>
          <Button
            type="text"
            size={controlSize}
            icon={<ReloadOutlined />}
            loading={busy === "analyze"}
            onClick={() => analyze(true)}
          />
        </Tooltip>
      </div>

      {analysis.phishingWarning && (
        <Alert
          type="error"
          showIcon
          icon={<WarningFilled />}
          className="mb-2"
          message={t("This email may be fraudulent")}
          description={analysis.phishingWarning}
        />
      )}

      <Paragraph className="mb-1">{analysis.summary}</Paragraph>
      {analysis.keyPoints?.length > 0 && (
        <ul className="mail-assistant-points">
          {analysis.keyPoints.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      )}
      {analysis.suggestedAction && (
        <Paragraph className="mb-1">
          <Text strong>{t("Next step")}: </Text>
          {analysis.suggestedAction}
        </Paragraph>
      )}
      {partial && (
        <Text type="secondary" className="mail-assistant-note">
          {analysis.truncated ? t("The email was too long to read completely.") + " " : ""}
          {analysis.unreadAttachments?.length
            ? t("Attachments judged by name only: {{count}}.", {
                count: analysis.unreadAttachments.length
              })
            : ""}
        </Text>
      )}
      {analysis.suggestedMailFolder && (
        <div className="mt-2">
          <Button
            size={controlSize}
            icon={<FolderOpenOutlined />}
            onClick={() => onMove(analysis.suggestedMailFolder)}
          >
            {t("Move to {{folder}}", { folder: analysis.suggestedMailFolder })}
          </Button>
        </div>
      )}

      <Collapse
        // Remounted per message so the sections open by what this message needs; the
        // rows in `drafts` arrive one render later, so this reads the analysis directly.
        key={`${message.uid}-${analysis.analyzedAt}`}
        ghost
        size="small"
        className="mail-assistant-sections"
        defaultActiveKey={[
          ...(analysis.requiresReply ? ["reply"] : []),
          ...(buildDrafts(analysis, message).some(item => item.selected) ? ["files"] : [])
        ]}
        items={[
          {
            key: "reply",
            label: t("Suggested reply"),
            children: (
              <>
                {analysis.replyStatus === "failed" && (
                  <Alert
                    type="error"
                    showIcon
                    className="mb-2"
                    message={t(analysis.replyError || "The reply could not be drafted.")}
                  />
                )}
                <Spin spinning={replyBusy} tip={t("Drafting…")}>
                  {analysis.reply?.body ? (
                    <div className="mail-assistant-reply">{analysis.reply.body}</div>
                  ) : (
                    <Text type="secondary">{t("No reply needed.")}</Text>
                  )}
                </Spin>
                <Space className="mt-2" wrap>
                  <Button
                    type="primary"
                    size={controlSize}
                    disabled={!analysis.reply?.body || replyBusy}
                    onClick={openReply}
                  >
                    {t("Use this reply")}
                  </Button>
                </Space>
                <Space.Compact className="mt-2 w-100">
                  <Input
                    size={controlSize}
                    value={instructions}
                    maxLength={500}
                    placeholder={t(
                      "How should the reply change? e.g. decline politely, propose Tuesday"
                    )}
                    onChange={event => setInstructions(event.target.value)}
                    onPressEnter={() => !replyBusy && redraft()}
                  />
                  <Button size={controlSize} loading={replyBusy} onClick={redraft}>
                    {analysis.reply?.body ? t("Redraft") : t("Draft a reply")}
                  </Button>
                </Space.Compact>
                <Text type="secondary" className="mail-assistant-note d-block mt-1">
                  {t("Check the draft before sending. Text in [brackets] needs your input.")}
                </Text>
              </>
            )
          },
          ...(drafts.length
            ? [
                {
                  key: "files",
                  label: t("Attachments → document directory ({{count}})", {
                    count: drafts.length
                  }),
                  children: (
                    <>
                      {drafts.map(item => (
                        <div key={item.index} className="mail-assistant-file">
                          <div className="mail-assistant-file-head">
                            {item.saved ? (
                              <CheckCircleFilled className="mail-assistant-saved" />
                            ) : (
                              <Checkbox
                                checked={item.selected}
                                onChange={event =>
                                  setDraft(item.index, { selected: event.target.checked })
                                }
                              />
                            )}
                            <Text strong ellipsis className="mail-assistant-file-name">
                              {item.filename}
                            </Text>
                            <Text type="secondary">{formatSize(item.size)}</Text>
                            {item.documentType && <Tag>{item.documentType}</Tag>}
                          </div>
                          {item.saved ? (
                            <Text type="secondary" className="mail-assistant-note">
                              {t("Saved in {{path}}", { path: item.savedPath })} ·{" "}
                              <Link to="/user/directory">{t("Open directory")}</Link>
                            </Text>
                          ) : (
                            <>
                              <div className="mail-assistant-file-fields">
                                <AutoComplete
                                  size={controlSize}
                                  value={item.directoryPath}
                                  options={directoryOptions.filter(option =>
                                    option.value
                                      .toLowerCase()
                                      .includes((item.directoryPath || "").toLowerCase())
                                  )}
                                  onChange={value => setDraft(item.index, { directoryPath: value })}
                                  placeholder={t("Folder, e.g. Rechnungen/2026")}
                                  className="mail-assistant-path"
                                />
                                <Input
                                  size={controlSize}
                                  value={item.fileName}
                                  onChange={event =>
                                    setDraft(item.index, { fileName: event.target.value })
                                  }
                                  placeholder={t("File name")}
                                />
                              </div>
                              {item.reason && (
                                <Text type="secondary" className="mail-assistant-note">
                                  {item.reason}
                                  {item.directoryPath &&
                                  assistant.directory &&
                                  !assistant.directory.some(
                                    folder =>
                                      folder.path.toLowerCase() === item.directoryPath.toLowerCase()
                                  )
                                    ? ` · ${t("New folder")}`
                                    : ""}
                                </Text>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                      <Button
                        type="primary"
                        size={controlSize}
                        className="mt-2"
                        disabled={
                          !selected.length || selected.some(item => !item.directoryPath.trim())
                        }
                        loading={busy === "save"}
                        onClick={saveSelected}
                      >
                        {t("Save {{count}} to directory", { count: selected.length })}
                      </Button>
                    </>
                  )
                }
              ]
            : [])
        ]}
      />
    </div>
  );
};

export default AssistantPanel;
